import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  useBreakpointValue,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import AudioRecorder, { AudioRecorderRef } from './AudioRecorder.component';

type AudioRecorderModalProps = {
  isOpen?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onConfirm: (file: File) => Promise<void>;
};

const AudioRecorderModal: React.FC<AudioRecorderModalProps> = ({
  isOpen = false,
  onClose = () => null,
  onConfirm,
  isLoading,
}) => {
  const recorderRef = React.useRef<AudioRecorderRef>(null);
  const modalSize = useBreakpointValue({ base: 'full', lg: 'lg' });
  const { t } = useTranslation();

  const handleConfirm = React.useCallback(async () => {
    const file = await recorderRef.current?.onSave();
    if (file) {
      await onConfirm(file);
    }
  }, [onConfirm]);

  const handleClose = React.useCallback(() => {
    recorderRef.current?.onClear();
    onClose();
  }, [onClose]);

  return (
    <Modal
      closeOnEsc={!isLoading}
      closeOnOverlayClick={!isLoading}
      size={modalSize}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <ModalOverlay />
      <Portal>
        <ModalContent>
          <ModalHeader>Gravar áudio</ModalHeader>
          <ModalCloseButton disabled={isLoading} />
          <ModalBody>
            <AudioRecorder ref={recorderRef} />
          </ModalBody>
          <ModalFooter justifyContent="flex-end">
            <Button
              isLoading={isLoading}
              translate="no"
              onClick={handleClose}
              variant="ghost"
              mr={3}
            >
              {t('common.cancel')}
            </Button>
            <Button
              isLoading={isLoading}
              colorScheme="brand"
              onClick={handleConfirm}
              translate="no"
            >
              {t('common.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Portal>
    </Modal>
  );
};

export default AudioRecorderModal;
